import { startRegistration } from '@simplewebauthn/browser';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Download, KeyRound, Trash } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { SessionDTO, SessionState, UserDTO } from '@harness/shared';
import { Modal, PageHeader, Spinner } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import { relTime } from '../../lib/format';
import { qk } from '../../lib/keys';
import { useSession } from '../../lib/queries';
import { toast } from '../../lib/store';
import { checkSupabaseFromBrowser } from '../../lib/supabase';

function Section({ title, children, sub }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <section className="card card-pad stack-sm">
      <div className="section-title">{title}</div>
      {sub && <div className="muted" style={{ fontSize: 13 }}>{sub}</div>}
      {children}
    </section>
  );
}

function uaLabel(ua: string) {
  const b = /Firefox\//.test(ua) ? 'Firefox' : /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : ua ? ua.split(/[ /]/)[0] : 'Unknown client';
  const os = /Mac OS X/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${b} on ${os}` : b;
}

export default function AccountPage() {
  const qc = useQueryClient();
  const user = useSession().data?.user;
  const [name, setName] = useState(user?.name ?? '');
  useEffect(() => setName(user?.name ?? ''), [user?.name]);

  const patchMe = async (body: Partial<Pick<UserDTO, 'name'>>) => {
    try {
      const u = await api.patch<UserDTO>('/api/auth/me', body);
      qc.setQueryData<SessionState>(qk.session, (s) => (s ? { ...s, user: u } : s));
      return u;
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  if (!user) return null;
  return (
    <div className="settings-content narrow">
      <PageHeader
        title="Account & security"
        sub={`Signed in as ${user.email}${user.organisation ? ` · ${user.role ? user.role[0]!.toUpperCase() + user.role.slice(1) : 'Member'} of ${user.organisation.name}` : ' · not in an organisation yet'}`}
      />

      <Section title="Profile">
        <div className="row" style={{ flexWrap: 'nowrap', maxWidth: 520 }}>
          <input className="input" aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <button className="btn btn-secondary" disabled={!name.trim() || name === user.name} onClick={() => void patchMe({ name: name.trim() }).then((u) => u && toast('Name saved'))}>
            Save
          </button>
        </div>
      </Section>

      <PasswordSection />
      <TwoFactorSection user={user} />
      <PasskeysSection />
      <SessionsSection />
      <SupabaseSection />
    </div>
  );
}

function PasswordSection() {
  const [f, setF] = useState({ current: '', next: '' });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post('/api/auth/password', f);
      setF({ current: '', next: '' });
      toast('Password changed · other sessions signed out');
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section title="Password">
      <div className="grid-2" style={{ maxWidth: 720 }}>
        <div className="field">
          <label htmlFor="pw-cur">Current password</label>
          <input id="pw-cur" className="input" type="password" autoComplete="current-password" value={f.current} onChange={(e) => setF({ ...f, current: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="pw-new">New password</label>
          <input id="pw-new" className="input" type="password" autoComplete="new-password" value={f.next} onChange={(e) => setF({ ...f, next: e.target.value })} />
          <div className="hint">At least 10 characters.</div>
        </div>
      </div>
      <div>
        <button className="btn btn-secondary" disabled={busy || !f.current || f.next.length < 10} onClick={save}>
          {busy && <Spinner />}Change password
        </button>
      </div>
    </Section>
  );
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const text = codes.join('\n');
  return (
    <Modal open onClose={onDone} label="Recovery codes" width={480}>
      <div className="kicker">Save these now</div>
      <div className="dialog-title">Recovery codes</div>
      <div className="dialog-body">Each code signs you in once if you lose your authenticator and passkeys. They won’t be shown again.</div>
      <div className="codes-grid">
        {codes.map((c) => (
          <span key={c}>{c}</span>
        ))}
      </div>
      <div className="dialog-actions" style={{ justifyContent: 'space-between' }}>
        <div className="row">
          <button className="btn btn-secondary" onClick={() => void navigator.clipboard.writeText(text).then(() => toast('Copied'))}>
            <Copy className="icon" />
            Copy
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => {
              const a = document.createElement('a');
              a.href = URL.createObjectURL(new Blob([`AI Harness recovery codes\n\n${text}\n`], { type: 'text/plain' }));
              a.download = 'harness-recovery-codes.txt';
              a.click();
            }}
          >
            <Download className="icon" />
            Download
          </button>
        </div>
        <button className="btn btn-primary" onClick={onDone}>
          I’ve saved them
        </button>
      </div>
    </Modal>
  );
}

function TwoFactorSection({ user }: { user: UserDTO }) {
  const qc = useQueryClient();
  const [setup, setSetup] = useState<{ secret: string; qr: string } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [disabling, setDisabling] = useState(false);
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');

  const start = async () => {
    try {
      setSetup(await api.post('/api/auth/2fa/setup'));
      setCode('');
      setErr('');
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };
  const enable = async () => {
    try {
      const r = await api.post<{ recoveryCodes: string[] }>('/api/auth/2fa/enable', { code });
      setSetup(null);
      setCodes(r.recoveryCodes);
      await qc.invalidateQueries({ queryKey: qk.session });
      toast('Two-step verification is on');
    } catch (e) {
      setErr(errorText(e));
    }
  };
  const regenerate = async () => {
    try {
      const r = await api.post<{ recoveryCodes: string[] }>('/api/auth/2fa/recovery-codes');
      setCodes(r.recoveryCodes);
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };
  const disable = async () => {
    try {
      await api.post('/api/auth/2fa/disable', { password: pw });
      setDisabling(false);
      setPw('');
      await qc.invalidateQueries({ queryKey: qk.session });
      toast('Authenticator app removed');
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  return (
    <Section title="Two-step verification" sub="Required before connecting any server or adding provider keys. Use an authenticator app, a passkey, or both.">
      <div className="status-line">
        <span className={`dot ${user.mfaEnabled || user.passkeyCount ? 'dot-online' : 'dot-unreachable'}`} />
        <span style={{ flex: 1 }}>
          {user.mfaEnabled ? 'Authenticator app is on' : 'Authenticator app is off'}
          {user.passkeyCount > 0 && ` · ${user.passkeyCount} passkey${user.passkeyCount === 1 ? '' : 's'}`}
        </span>
        {user.mfaEnabled ? (
          <>
            <button className="btn btn-secondary" onClick={regenerate}>
              New recovery codes
            </button>
            <button className="btn btn-ghost btn-danger" onClick={() => setDisabling(true)}>
              Turn off
            </button>
          </>
        ) : (
          !setup && (
            <button className="btn btn-primary" onClick={start}>
              Set up authenticator app
            </button>
          )
        )}
      </div>
      {setup && (
        <div className="tile tile-neutral" style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap', padding: 16 }}>
          <img className="qr" src={setup.qr} alt="QR code for your authenticator app" />
          <div className="stack-sm" style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontWeight: 600 }}>Scan with your authenticator app</div>
            <div className="muted" style={{ fontSize: 13 }}>
              Or enter this key by hand:
            </div>
            <code className="mono" style={{ fontSize: 13, wordBreak: 'break-all' }}>
              {setup.secret.replace(/(.{4})/g, '$1 ').trim()}
            </code>
            <div className="field" style={{ maxWidth: 260 }}>
              <label htmlFor="tf-code">6-digit code</label>
              <input
                id="tf-code"
                className="input otp"
                style={{ fontSize: 22, minHeight: 46 }}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                placeholder="000000"
                onChange={(e) => (setCode(e.target.value.replace(/\D/g, '').slice(0, 6)), setErr(''))}
                onKeyDown={(e) => e.key === 'Enter' && code.length === 6 && void enable()}
              />
            </div>
            {err && <div className="err">{err}</div>}
            <div className="row">
              <button className="btn btn-primary" disabled={code.length !== 6} onClick={enable}>
                Turn on
              </button>
              <button className="btn btn-ghost" onClick={() => setSetup(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {codes && <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />}
      <Modal open={disabling} onClose={() => setDisabling(false)} label="Turn off authenticator">
        <div className="dialog-title">Turn off the authenticator app?</div>
        <div className="dialog-body">Not allowed while any server connection or provider exists. Confirm with your password.</div>
        <input className="input" type="password" autoFocus aria-label="Password" value={pw} onChange={(e) => setPw(e.target.value)} />
        <div className="dialog-actions">
          <button className="btn btn-ghost" onClick={() => setDisabling(false)}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={!pw} onClick={disable}>
            Turn off
          </button>
        </div>
      </Modal>
    </Section>
  );
}

function PasskeysSection() {
  const qc = useQueryClient();
  const keys = useQuery({ queryKey: qk.passkeys, queryFn: () => api.get<{ id: string; name: string; createdAt: string }[]>('/api/auth/passkeys') });
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);

  const add = async () => {
    setBusy(true);
    try {
      const optionsJSON = await api.post<Parameters<typeof startRegistration>[0]['optionsJSON']>('/api/auth/passkeys/register/options');
      const response = await startRegistration({ optionsJSON });
      const r = await api.post<{ recoveryCodes: string[] | null }>('/api/auth/passkeys/register/verify', { response, name: name.trim() || undefined });
      setName('');
      await qc.invalidateQueries({ queryKey: qk.passkeys });
      await qc.invalidateQueries({ queryKey: qk.session });
      toast('Passkey added');
      if (r.recoveryCodes) setCodes(r.recoveryCodes);
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id: string) => {
    try {
      await api.del(`/api/auth/passkeys/${encodeURIComponent(id)}`);
      await qc.invalidateQueries({ queryKey: qk.passkeys });
      await qc.invalidateQueries({ queryKey: qk.session });
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  return (
    <Section title="Passkeys" sub="Sign in with Touch ID, Windows Hello or a security key as your second step.">
      <div>
        {(keys.data ?? []).map((k) => (
          <div key={k.id} className="row-line" style={{ gridTemplateColumns: '20px 1fr auto' }}>
            <KeyRound className="icon" />
            <div>
              <div style={{ fontWeight: 600 }}>{k.name}</div>
              <div className="faint" style={{ fontSize: 12 }}>
                Added {relTime(k.createdAt).toLowerCase()}
              </div>
            </div>
            <button className="btn btn-icon sm" title="Remove passkey" onClick={() => void remove(k.id)}>
              <Trash className="icon" />
            </button>
          </div>
        ))}
        {keys.data?.length === 0 && <div className="faint" style={{ fontSize: 13, padding: '4px 0 8px' }}>No passkeys yet.</div>}
      </div>
      <div className="row" style={{ flexWrap: 'nowrap', maxWidth: 520 }}>
        <input className="input" placeholder="Name, e.g. MacBook Touch ID" aria-label="Passkey name" value={name} onChange={(e) => setName(e.target.value)} />
        <button className="btn btn-secondary" onClick={add} disabled={busy}>
          {busy ? <Spinner /> : <KeyRound className="icon" />}
          Add passkey
        </button>
      </div>
      {codes && <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />}
    </Section>
  );
}

function SessionsSection() {
  const qc = useQueryClient();
  const sessions = useQuery({ queryKey: qk.sessions, queryFn: () => api.get<SessionDTO[]>('/api/auth/sessions') });
  const revoke = async (id: string) => {
    try {
      await api.del(`/api/auth/sessions/${id}`);
      await qc.invalidateQueries({ queryKey: qk.sessions });
      toast('Session signed out');
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };
  return (
    <Section title="Active sessions" sub="Sessions last 7 days from last use. Revoke any you don’t recognise.">
      <table className="table">
        <thead>
          <tr>
            <th>Device</th>
            <th>IP</th>
            <th>Signed in</th>
            <th>Last active</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {(sessions.data ?? []).map((s) => (
            <tr key={s.id}>
              <td>
                {uaLabel(s.userAgent)}
                {s.current && <span className="tag tag-accent sm" style={{ marginLeft: 8 }}>This session</span>}
              </td>
              <td className="mono" style={{ fontSize: 12 }}>
                {s.ip}
              </td>
              <td>{relTime(s.createdAt)}</td>
              <td>{relTime(s.lastSeenAt)}</td>
              <td style={{ textAlign: 'right' }}>
                {!s.current && (
                  <button className="btn btn-ghost" onClick={() => void revoke(s.id)}>
                    Revoke
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}

interface SupabaseStatus {
  configured: boolean;
  url: string | null;
  projectRef: string | null;
  ok: boolean;
  latencyMs: number;
  message: string;
}

// Phase 1 of the Supabase integration: shows the connection is set up; nothing uses it yet.
function SupabaseSection() {
  const server = useQuery({ queryKey: ['supabase-status'], queryFn: () => api.get<SupabaseStatus>('/api/supabase/status'), staleTime: 60_000 });
  const browser = useQuery({ queryKey: ['supabase-browser'], queryFn: checkSupabaseFromBrowser, staleTime: 60_000 });
  const row = (label: string, s: { ok: boolean; latencyMs: number; message: string } | undefined, loading: boolean) => (
    <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'nowrap', gap: 12 }}>
      <span>{label}</span>
      {loading || !s ? (
        <Spinner />
      ) : (
        <span className={s.ok ? undefined : 'muted'} style={{ fontSize: 13, textAlign: 'right' }}>
          <span className={`dot ${s.ok ? 'dot-online' : 'dot-offline'}`} /> {s.message}
          {s.ok ? ` · ${s.latencyMs} ms` : ''}
        </span>
      )}
    </div>
  );
  return (
    <Section title="Supabase" sub={server.data?.url ? `Project ${server.data.projectRef ?? server.data.url}` : 'Control plane for the harness. Connection only for now; data still lives in the local database.'}>
      {row('Server (secret key)', server.data, server.isLoading)}
      {row('Browser (publishable key)', browser.data, browser.isLoading)}
    </Section>
  );
}
